import { create } from "zustand";
import { Menu, MenuItem } from "../dashboard/menu/menu";
import menu from "../dashboard/menu/menu";
import footerMenu from "../dashboard/menu/footerMenu";
import MenuByRoleBuilderService from "../authUser/auth/services/MenuByRoleBuilderService/MenuByRoleBuilderService";
import MapDashboardMenuService from "../../app/shared/services/dashboard/menu/mapDashboardMenuIconsService/MapDashboardMenuService";
import AuthData from "../authUser/auth/entities/AuthData";
import { recordMenuRebuild } from "@/app/shared/permissions/permissionTelemetry";

export interface MenuState {
  // State
  menuItems: Menu;
  footerMenuItems: Menu;
  isLoading: boolean;
  error: string | null;

  // Actions
  buildMenu: (user: AuthData) => void;
  buildFooterMenu: (user: AuthData) => void;
  setMenuItems: (menu: Menu) => void;
  setFooterMenuItems: (menu: Menu) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  /** Clears menus and menu error on logout / session end (avoids stale items for next user). */
  resetMenuForSession: () => void;

  // Computed values
  hasMenuItems: boolean;
  hasFooterMenuItems: boolean;
  getMenuBySection: () => Record<string, MenuItem[]>;
}

export const useMenuStore = create<MenuState>((set, get) => ({
  // Initial state
  menuItems: [],
  footerMenuItems: [],
  isLoading: false,
  error: null,

  // Actions
  buildMenu: (_user: AuthData) => {
    // console.log("🔧 MenuStore - buildMenu called with user:", user);
    set({ isLoading: true, error: null });

    try {
      const menuService = new MenuByRoleBuilderService(menu);

      const builtMenu = menuService.buildMenu();

      // Transform menu items to add icons and paths using MapDashboardMenuService
      const mapService = new MapDashboardMenuService(builtMenu);
      const mappedMenu = mapService.map();

      set({
        menuItems: mappedMenu,
        isLoading: false,
      });
      recordMenuRebuild({
        kind: "main",
        visibleTopLevelCount: mappedMenu.length,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Failed to build menu",
        isLoading: false,
      });
    }
  },

  buildFooterMenu: (_user: AuthData) => {
    // console.log("🔧 MenuStore - buildFooterMenu called with user:", user);

    try {
      const menuService = new MenuByRoleBuilderService(footerMenu);

      const builtFooterMenu = menuService.buildMenu();

      // Transform footer menu items to add icons and paths using MapDashboardMenuService
      const mapService = new MapDashboardMenuService(builtFooterMenu);
      const mappedFooterMenu = mapService.map();

      set({
        footerMenuItems: mappedFooterMenu,
      });
      recordMenuRebuild({
        kind: "footer",
        visibleTopLevelCount: mappedFooterMenu.length,
      });
    } catch (error) {
      console.error("Failed to build footer menu:", error);
      set({
        footerMenuItems: [],
      });
    }
  },

  setMenuItems: (menu: Menu) => {
    set({ menuItems: menu });
  },

  setFooterMenuItems: (menu: Menu) => {
    set({ footerMenuItems: menu });
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setError: (error: string | null) => {
    set({ error, isLoading: false });
  },

  clearError: () => {
    set({ error: null });
  },

  resetMenuForSession: () => {
    set({
      menuItems: [],
      footerMenuItems: [],
      isLoading: false,
      error: null,
    });
  },

  // Computed values
  get hasMenuItems() {
    return get().menuItems.length > 0;
  },

  get hasFooterMenuItems() {
    return get().footerMenuItems.length > 0;
  },

  getMenuBySection: () => {
    const { menuItems } = get();
    // Group menu items by section (you can enhance this based on your menu structure)
    return menuItems.reduce((acc, item) => {
      // This is a simplified grouping - you can enhance based on your needs
      const section = "main";
      if (!acc[section]) acc[section] = [];
      acc[section].push(item);
      return acc;
    }, {} as Record<string, MenuItem[]>);
  },
}));
